import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getTranslationFile, saveTranslationFile, uploadTranslationFile } from '../../../connection/translations';
import ModalComponent from '../../general-ui/modal/modal';
import LargeButton from '../../general-ui/button/large-button';
import { showAlert, showAlertLong } from '../../general-ui/alert/alert-service';
import LanguageSelectInput from '../../general-ui/select/language-select';
import { PrimaryTooltip } from '../create/session/session-modal';
import TranslateLangs from '../../../utils/translate-langs.json';
import { setTranslationManifests, updateWorkingEvent } from '../../../store/actions/admin/create-event';
import { useDefaultEventLanguage } from '../../../utils/use-default-event-language';
import { Language, TranslationUploadWarning } from '../../../types/working-model';
import SelectInput from '../../general-ui/select/select';
import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion";
import translateLangsMap from '../../../utils/translate-langs-map.json';
import { LanguagesAbbr } from '../../../types/working-model';
import { toggleTranslationsV2 } from '../../../store/actions/admin/create-event';
import WaitingIndicator from '../../general-ui/waiting-indicator/waiting-indicator';
import { toArrayMap, userToConnectedUser } from '../../../utils/utils';
import Switch from "../../general-ui/switch/switch";
import { useAppDispatch, useThunkDispatch, useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { streamsLoader } from '../../../i18n/config';
import { SaveSessionList } from '../../../connection/create-event';
import Socket from '../../../connection/socket';
import SocketManager from "../../../connection/socket-main-thread/socket-manager";

import './event-translations-modal.scss';

interface EventTranslationsModalProps {
	open: boolean;
	closeFn: () => void;
	languages: string[];
}

const getLangWarning = (uploadWarnings: TranslationUploadWarning[]) => {
	const langWarnings = uploadWarnings.filter(warning => warning.type === 'Incorrect Language');
	const langGroups = toArrayMap('value', langWarnings);
	return Array.from(langGroups.entries()).sort((a, b) => a[1] < b[1] ? 1 : -1)[0] as [string, TranslationUploadWarning[]];
};

const rmHtml = (str: string) => {
	const div = document.createElement('div');
	div.innerHTML = str;
	return div.innerText;
};

const EventTranslationsModal = ({ open, closeFn, languages }: EventTranslationsModalProps): JSX.Element => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const savingEvent = useTypedSelector(state => state.CreateEventReducer.saving);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const translationVersion = useTypedSelector((state) => state.CreateEventReducer.workingEvent?.settings?.translation_version);

	const dispatch = useAppDispatch();
	const thunkDispatch = useThunkDispatch();

	const defaultEventLanguage = useDefaultEventLanguage();

	const sessionLanguages = useMemo(() => {
		const { sessions } = workingEvent ?? {};

		const sessionLanguageCodes = new Set<string>();
		sessions?.forEach(session => {
			const { languages } = session;
			languages.forEach(lang => sessionLanguageCodes.add(lang));
		});

		const availableLanguages: Language[] = [];

		Array.from(sessionLanguageCodes).forEach(sessionLangCode => {
			const found = TranslateLangs.find(lang => lang.code === sessionLangCode);

			if (found) {
				availableLanguages.push(found);
			}
		});

		return availableLanguages;
	}, [workingEvent]);

	const [uploadWarnings, setUploadWarnings] = useState<TranslationUploadWarning[]>([]);
	const [uploadComplete, setUploadComplete] = useState(false);
	const [processingUpload, setProcessingUpload] = useState(false);
	const [selectedLanguage, setSelectedLanguage] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [savingEventLanguage, setSavingEventLanguage] = useState(false);
	const [saving, setSaving] = useState(false);
	const [downloading, setDownloading] = useState(false);

	const socket = useRef<Socket | null>(null);

	const activeChannel = useMemo(() => user?.active_channel, [user]);

	useEffect(() => {
		if (activeChannel && user && workingEvent) {
			socket.current = SocketManager.get(`admin-${activeChannel}`, userToConnectedUser(user, workingEvent.uuid));
		}
	}, [dispatch, activeChannel, user, workingEvent]);

	const downloadFn = async () => {
		if (!token || !workingEvent) return;

		setDownloading(true);
		getTranslationFile(token, workingEvent.uuid)
			.then(resp => {
				if (resp.status === 200) {
					return resp.blob();
				}
				throw ('Unable to fetch report');
			})
			.then(blob => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.style.display = 'none';
				a.href = url;
				a.download = `${workingEvent?.name}_Translations.zip`;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url);
			})
			.catch((e) => {
				console.error(e);
				showAlert({ message: 'Download failed', description: 'Your translations failed to download.', duration: 3000, type: 'error' });
				console.error('Translations download failed for event: ', workingEvent.uuid);
			}).finally(() => {
				setDownloading(false);
			});
	};

	const handleFile = async (file: File | FileList) => {
		if (file instanceof File && token && workingEvent) {
			setSelectedFile(file);
		}
	};

	const commitUpload = async () => {
		if (selectedFile && selectedLanguage && token && workingEvent) {
			try {
				if (user && socket.current) {
					socket.current.sendMessage({
						type: 'translations-updating',
						data: {
							isUpdating: true,
							admin: {
								profile_id: user.id,
								first_name: user.profile.first_name,
								last_name: user.profile.last_name
							},
							eventUuid: workingEvent.uuid
						}
					});
				}
				setUploadWarnings([]);
				setUploadComplete(false);
				setProcessingUpload(true);
				const res = await uploadTranslationFile(token, selectedFile, workingEvent.uuid, selectedLanguage);
				dispatch(setTranslationManifests(res.manifests));
				setProcessingUpload(false);
				setUploadWarnings(res.warnings);
				setUploadComplete(true);
			} catch (e: unknown) {
				setProcessingUpload(false);
				showAlertLong({
					message: 'Unable to process upload',
					description: (e as { error: { message: string } }).error.message,
					type: 'error'
				});
			} finally {
				if (socket.current) {
					socket.current.sendMessage({
						type: 'translations-updating',
						data: {
							isUpdating: false,
							admin: null,
							eventUuid: workingEvent.uuid
						}
					});
				}
			}
		}
	};

	/**
	 * Update the default language of all sessions for this event
	 */
	const updateSessions = useCallback(async (languageCode: string): Promise<void> => {
		if (!token || !workingEvent?.sessions) return;

		const sessions = workingEvent.sessions.map(s => ({
			...s,
			default_language: languageCode,
			languages: s.languages.includes(languageCode) ? s.languages : [languageCode, ...s.languages]
		}));

		try {
			await SaveSessionList(sessions, token);
		} catch (e) {
			console.error(e);
		}

	}, [token, workingEvent?.sessions]);

	/**
	 * Update the working event's base language.
	 */
	const handleLanguageChange = useCallback(async (_: Language[], defaultLang: Language) => {
		if (token && defaultLang && workingEvent && defaultLang.code !== workingEvent?.settings?.i18n?.base) {
			setSavingEventLanguage(true);
			await updateSessions(defaultLang.code);
			thunkDispatch(updateWorkingEvent({
				...workingEvent,
				settings: {
					...workingEvent.settings,
					i18n: {
						base: defaultLang.code
					}
				}
			}, token));
			setSavingEventLanguage(false);
		}
	}, [thunkDispatch, token, updateSessions, workingEvent]);

	const close = useCallback(() => {
		setSelectedLanguage('');
		setSelectedFile(null);
		closeFn();
	}, [closeFn]);

	const uploadWarningMessage = useMemo(() => {
		const missingKeyWarnings: JSX.Element[] = [];
		const missingCellWarnings: JSX.Element[] = [];

		for (const [key, value] of uploadWarnings.entries()) {
			if (value.type === "Missing Key") {
				missingKeyWarnings.push(
					<div key={`key-warning-${key}`}>
						<i>{value.value}</i>
					</div>
				);
			}
			if (value.type === "Missing Cell") {
				missingCellWarnings.push(
					<div key={`cell-warning-${key}`}>
						<i>{value.value}</i>
					</div>
				);
			}
		}
		return (
			(missingKeyWarnings.length || missingCellWarnings.length) ? (
				<div className='upload-warnings-container'>
					{missingKeyWarnings.length > 0 ? (
						<div className="missing-key-warnings">
							<h6>The following rows were missing from the upload</h6>
							<div className="missing-keys">
								{missingKeyWarnings}
							</div>
						</div>
					) : null}
					{missingCellWarnings.length > 0 ? (
						<div className="missing-cell-warnings">
							<h6>The following cells were missing from the upload</h6>
							<div className="missing-keys">
								{missingCellWarnings}
							</div>
						</div>
					) : null}
				</div>
			) : null
		);
	}, [uploadWarnings]);

	const changeLanguage = (value: string) => {
		setSelectedLanguage(value);
	};

	const languageOptions = useMemo(() => {
		return [{ label: "Select Language", value: "" }, ...languages.map(
			lang => (
				{
					label: `${translateLangsMap[lang as unknown as LanguagesAbbr]} - ${lang}`,
					value: lang
				}
			)
		)];
	}, [languages]);

	const applyTranslations = useCallback(async () => {
		if (!token || !workingEvent) {
			return;
		}

		try {
			setSaving(true);
			const saved = await saveTranslationFile(token, workingEvent.uuid);

			if (!saved) {
				throw new Error(`Unable to save translation file for ${workingEvent.uuid}`);
			}

			setUploadWarnings([]);
			setUploadComplete(false);
			streamsLoader.refresh();
			close();
		} catch (e) {
			console.error(e);
			showAlertLong({
				message: 'Unable to apply translations',
				description: 'We ran into an issue applying your translations. Please try again later.',
				type: 'error'
			});
		} finally {
			setSaving(false);
		}
	}, [close, token, workingEvent]);

	useEffect(() => {
		if (!workingEvent || !token) {
			return;
		}

		// the translation version is 2 and there has never been a default event language set on this event
		// so set one. 
		if (workingEvent.settings.translation_version === 2 && !workingEvent.settings.i18n?.base) {
			handleLanguageChange([], defaultEventLanguage);
		}
	}, [workingEvent, token, handleLanguageChange, defaultEventLanguage]);

	const incorrectLanguageWarning = useMemo(() => {
		const incorrectLanguage = getLangWarning(uploadWarnings);

		if (incorrectLanguage?.[0]) {
			const meta = incorrectLanguage[1]?.[0].metadata as { sheet: string, key: string, value: string };
			return (
				<>
					<div className="missing-language-warning">
						<h4>Incorrect Language Detected</h4>
						<p><strong className="error">{incorrectLanguage[0]}</strong></p>
						<h6>Example:</h6>
						<blockquote>
							<section>
								<label>Sheet</label>
								<p><strong>{meta?.sheet}</strong></p>
							</section>

							<section>
								<label>Key</label>
								<p>{meta?.key}</p>
							</section>

							<section>
								<label>Entry</label>
								<p><em>{rmHtml(meta?.value)}</em></p>
							</section>
						</blockquote>
					</div>
				</>
			);
		}
	}, [uploadWarnings]);

	const handleToggleForV2Translation = () => {
		const turnOnV2Translations = translationVersion !== 2;
		if (!turnOnV2Translations && workingEvent?.settings?.i18n?.base !== 'en') {
			showAlert({ message: 'Event change', description: "Resetting default event language", duration: 5000 });
		}
		dispatch(toggleTranslationsV2(turnOnV2Translations));
	};

	return (
		<ModalComponent
			title="Event Translations"
			open={open}
			padTitle={false}
			onRequestClose={close}
			className={"translations-modal"}
			closeable
			cancellable
			header={(
				<div className="row translations-toggle">
					<span className="switch-label"> Enable .xls Translations</span>
					<Switch
						value={'use-translation-v2'}
						on={translationVersion === 2 ? true : false}
						onClick={handleToggleForV2Translation}
					/>
				</div>
			)}
			footer={workingEvent?.settings.translation_version === 2 && (
				<div className="translations-footer">
					<button
						disabled={downloading}
						style={{ marginRight: 'auto' }}
						onClick={downloadFn}>
						{!downloading ? (
							<>
								Download Translations
							</>
						) : (
							<>
								<span style={{ marginRight: 6 }}>Generating file</span>
								<WaitingIndicator />
							</>
						)}
					</button>

					<div className="translations-footer-actions">
						<button style={{ marginLeft: 'auto' }} onClick={close}>Close</button>
						<button className="primary" disabled={!(selectedLanguage && selectedFile)} style={{ marginLeft: 'auto' }} onClick={commitUpload}>Upload</button>
					</div>
				</div>
			)}
		>
			{/* body modal */}
			<div className="translations-modal">
				{workingEvent?.settings.translation_version === 2 ? (
					<div>
						<LanguageSelectInput
							label={"Default Event Language"}
							tooltip={[PrimaryTooltip, "Your default language is used across the event to generate translations and as a fallback for text untranslated in a given language."]}
							placeholder={"Select language"}
							onChange={handleLanguageChange}
							languages={[defaultEventLanguage]}
							defaultLanguage={defaultEventLanguage}
							limit={1}
							chooseableLanguages={sessionLanguages}
							disableDefault
							disableInput={savingEventLanguage || savingEvent}
							disableDefaultRemoval
						/>
						<LargeButton
							style={{ marginTop: 0 }}
							onFile={handleFile}
							title="Select translation document"
							allowedFileTypes={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}
							uploading={processingUpload}
						/>
						<AnimatePresence>
							{!!selectedFile && (
								<LazyMotion features={domAnimation}>
									<m.div
										initial={{ maxHeight: 0, opacity: 0 }}
										animate={{ maxHeight: 200, opacity: 1 }}
										exit={{ maxHeight: 0, opacity: 0 }}
										className="translations-language-select">
										<SelectInput
											label={"Select Language"}
											options={languageOptions}
											selected={selectedLanguage}
											onChange={changeLanguage}
											placeholder={"Select Excel file language"}
											ignoreInlineStyling={true}
										/>
									</m.div>
								</LazyMotion>
							)}
						</AnimatePresence>

						<div>
							<ModalComponent
								open={uploadComplete}
								closeable
								cancellable
								onRequestClose={() => setUploadComplete(false)}
								title={'Your upload has finished processing'}
								className={"post-processing-modal"}
								footer={
									<>
										<button onClick={() => {
											setUploadWarnings([]);
											setUploadComplete(false);
										}
										}>Reupload</button>
										<button onClick={() => {
											setUploadWarnings([]);
											setUploadComplete(false);
											close();
										}
										}>Close</button>
										<button
											className="primary"
											disabled={saving}
											style={{ marginLeft: 'auto' }}
											onClick={applyTranslations}
										>
											{saving ? (
												<WaitingIndicator />
											) : (
												"Apply Translations"
											)}
										</button>
									</>
								}
							>
								<div>
									{
										uploadComplete && !uploadWarningMessage && (
											<div className="upload-complete-message">
												<h5>Upload complete</h5>
											</div>
										)
									}
									{incorrectLanguageWarning}
									{uploadWarningMessage && (
										<div className="warning-messages">
											<h4>The following fields were not translated</h4>
											{uploadWarningMessage}
										</div>
									)}
								</div>
							</ModalComponent>
						</div>
					</div>
				) : (
					<div>
						<p>Toggle on to enable the .xls translation flow. This will enable you to add additional languages across your event and export all site content to a .xls for offline translation / localization and re-upload to the platform.</p>
						<p>After upload, translated content will be <strong>read-only</strong> and can be updated using the download / upload process.</p>
						<p>Click <a href="https://help.brand.live/hc/en-us/articles/8064590324631" target="_blank" rel="noreferrer">here</a> to read the full documentation on .xls translation workflow.</p>
					</div>
				)}
			</div>
		</ModalComponent>
	);
};

export default EventTranslationsModal;
